import { getComplaintCategories } from "../api/order-requests";
import i18n from "../i18n.js";

// The complaint categories of the active classifier version ({ code: "1.2", title, group, groupTitle }).
// Read once per session and language: a version changes rarely, and the Reviews table asks for its
// names on every render — but the titles are in the user's language, so a change of language reads
// them again rather than keeping the old ones. A failed read is not kept, so the next page load tries again.
let categories = [];
let language = null;
let loading = null;

const currentLanguage = () => i18n.language || "en";

export const loadComplaintCategories = () => {
    const wanted = currentLanguage();

    if (categories.length && language === wanted) return Promise.resolve(categories);
    if (loading && loading.language === wanted) return loading.promise;

    const promise = getComplaintCategories(wanted)
        .then(response => {
            if (response?.status === 200 && Array.isArray(response.data)) {
                categories = response.data;
                language = wanted;
            }
            return categories;
        }, () => categories)
        .finally(() => { if (loading && loading.promise === promise) loading = null; });

    loading = { language: wanted, promise };

    return promise;
}

// Options of the Reviews table's complaint filter, "Group · Category" so two categories of the same
// name in different groups can be told apart. The page saga loads the list first; opening the
// filter before that (or after a failed read, or a change of language) asks again, and the next
// render has it.
export const getComplaintCategoryOptions = () => {
    if (!categories.length || language !== currentLanguage()) loadComplaintCategories();

    return categories.map(c => ({ key: c.code, value: c.groupTitle ? `${c.groupTitle} · ${c.title}` : c.title }));
}

// "1.2.1" → "1.2"; a code the classifier stopped at the category is the category already
const categoryOf = code => String(code).split(".").slice(0, 2).join(".");

// The categories of an order's codes, once each and named where the version knows them
export const getComplaintCategoryTitles = codes => {
    if (language !== null && language !== currentLanguage()) loadComplaintCategories();

    return [...new Set((codes || []).map(categoryOf))]
        .map(code => categories.find(c => c.code === code)?.title || code);
}
